import { defineStore } from 'pinia';
import SettingService from '@/services/settings';
import deepEqual from 'fast-deep-equal/es6';

/** A named theme color of the widget */
export interface ThemeColor {
  /** Color slot name, e.g. "primary" */
  name: string;
  /** Hex color code, e.g. "#11243c" */
  color: string;
}

/** Widget appearance configuration */
export interface Appeareance {
  /** Widget theme variant, e.g. "regular" */
  themetype: string;
  /** Theme colors keyed by slot ("primary" / "secondary") */
  themecolors: Record<string, ThemeColor>;
  /** Chat-button styling: which theme color and text contrast to use */
  button: { color: string; contrast: string };
  /** Widget header styling: which theme color and text contrast to use */
  widgetheader: { color: string; contrast: string };
  /** User message bubble styling: which theme color and text contrast to use */
  usermessage: { color: string; contrast: string };
  /** Widget anchor position, e.g. "bottom right" */
  alignmentOrigin: string;
}

/** State of the channelSettings module (widget channel configuration editor) */
export interface ChannelSettingsState {
  /** Whether the channel config is being fetched */
  isLoading: boolean;
  /** Whether the channel config is being saved */
  isSaving: boolean;
  /** JSON snapshot of the last loaded/saved config, used for dirty checking */
  hash: string;
  /** Functional widget settings as returned by the API */
  settings: any;
  /** Widget appearance settings (may be replaced by `null` from the API response) */
  appeareance: Appeareance | null;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/channelSettings.ts`. Legacy vuex
 * registration key (and therefore this store's id) is the lowercase `channelsettings` — NOT the
 * filename's `channelSettings` — see STORES.md. In-memory only: no `vuexPersist` option (the
 * legacy module had no `vuexLocal`/`vuexSession` reducer entry).
 *
 * Collisions & drops (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy getters `isLoading`, `isSaving`, `appeareance` were pure identity passthroughs of the
 *   state props of the same name. Pinia exposes state props directly on the store instance
 *   (`useChannelsettingsStore().isLoading`), so — mirroring the precedent set in `src/stores/app.ts`
 *   for the same situation — these getters are DROPPED entirely rather than renamed; consumers
 *   read the state prop directly, which is exactly what the old getter returned.
 * - `vuex-map-fields` getter `getField` / mutation `updateField` are DROPPED. Pinia state is
 *   directly writable; consumer components must replace `mapFields(...)` bindings with a writable
 *   `computed` or `storeToRefs` against this store.
 * - All remaining mutations (`setLoading`, `setChannelConfig`, `setSettingStartScreen`,
 *   `setAppearanceValue`) become same-named actions that mutate `this` directly — no name
 *   collisions with the existing actions (`fetchChannelConfig`, `saveChannelConfig`,
 *   `changeChannelState`), so no merges were needed. Call sites inside this store that used to
 *   `commit(...)` these now call the sibling action via `this.xxx(...)`.
 */
export const useChannelsettingsStore = defineStore('channelsettings', {
  state: (): ChannelSettingsState => ({
    isLoading: false,
    isSaving: false,
    hash: '',
    settings: {},
    appeareance: {
      themetype: 'regular',
      themecolors: {
        primary: {
          name: 'primary',
          color: '#11243c',
        },
        secondary: {
          name: 'secondary',
          color: '#02c7b2',
        },
      },
      button: {
        color: 'primary',
        contrast: 'light',
      },
      widgetheader: {
        color: 'secondary',
        contrast: 'light',
      },
      usermessage: {
        color: 'primary',
        contrast: 'light',
      },
      alignmentOrigin: 'bottom right',
    },
  }),
  getters: {
    getThemeColors(state: any) {
      return state.appeareance.themecolors;
    },
    getThemeColorcodeByType: (state: any) => (type: string) =>
      state.appeareance.themecolors[type].color,
    gotConfig(state: ChannelSettingsState) {
      return state.settings;
    },
    getVersion(state: ChannelSettingsState) {
      return state.settings.version;
    },
    getDirty(state: ChannelSettingsState) {
      try {
        const hashJSON = JSON.parse(state.hash);
        const settingsJSON = {
          appeareance: state.appeareance,
          settings: state.settings,
        };
        return !deepEqual(hashJSON, settingsJSON);
      } catch (e) {
        return false;
      }
    },
  },
  actions: {
    async fetchChannelConfig(payload: { botId: string; channelId: string }) {
      this.setLoading(true);
      const settings = await SettingService.getChannelConfig(payload.botId, payload.channelId);
      this.setChannelConfig(settings);
      this.setLoading(false);
      return settings;
    },
    async saveChannelConfig(payload: { botId: string; channelId: string }) {
      const config = {
        appeareance: this.appeareance,
        settings: this.settings,
      };

      this.isSaving = true;

      const savedSettings = await SettingService.saveChannelConfig(
        payload.botId,
        payload.channelId,
        config,
      );
      this.setChannelConfig(savedSettings);
      this.isSaving = false;
      return savedSettings;
    },
    async changeChannelState(payload: { botId: string; channelId: string; state: any }) {
      const savedState = await SettingService.saveChannelState(
        payload.botId,
        payload.channelId,
        payload.state,
      );
      if (savedState.error) throw new Error(savedState.error);
      return savedState;
    },
    /**
     * Converted from legacy mutation `setLoading` (no matching legacy action of the same name, so
     * no merge was needed).
     */
    setLoading(payload: boolean) {
      this.isLoading = payload;
    },
    /**
     * Converted from legacy mutation `setChannelConfig` (no matching legacy action of the same
     * name, so no merge was needed).
     */
    setChannelConfig(payload: any) {
      // Setting config fetch from api
      this.appeareance = payload.appeareance || null;
      this.settings = payload.settings || {};

      this.hash = JSON.stringify({
        appeareance: this.appeareance,
        settings: this.settings,
      });
    },
    /**
     * Converted from legacy mutation `setSettingStartScreen` (no matching legacy action of the
     * same name, so no merge was needed).
     */
    setSettingStartScreen(payload: any) {
      // Delete it
      if (!payload && this.settings) {
        this.settings.startscreen = null;
        this.settings = { ...this.settings };
        return;
      }
      // Set it
      this.settings.startscreen = { ...payload };
      this.settings = { ...this.settings };
    },
    /**
     * Converted from legacy mutation `setAppearanceValue` (no matching legacy action of the same
     * name, so no merge was needed).
     */
    setAppearanceValue({ key, value }: { key: string; value: any }) {
      (this.appeareance as any)[key] = value;

      this.appeareance = { ...this.appeareance } as Appeareance;
    },
  },
});
